import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { DeleteWarning, DomHelpers, Help, Loading, Modal, Paging, SystemText } from '@salesperformanceint/e4shared';
import BlobImage from 'components/blobImage';
import NoResults from 'components/general/noResults';
import { apiDelete, apiPatch } from 'services';
import { setShowSuccess } from 'actions/ui';
import { isPast, endOfDay, e4Format, e4Formats } from 'helpers/dates';
import ScheduleEdit from '../scheduleEdit';
import TableSorter from 'components/general/tableSorter';

export const Reviews = ({
    data,
    page,
    status,
    setStatus,
    loading,
    setPage,
    reload,
    sortBy,
    setSortBy,
    descending,
    setDescending,
    selected,
    setSelected,
    selectAll,
    setSelectAll,
    users,
}) => {
    const dispatch = useDispatch();
    const formatDate = d => (d ? e4Format(d) : '-');
    const formatDateTime = d => (d ? `${e4Format(d)} ${e4Format(d, e4Formats.time)}` : '-');
    const [showPoke, setShowPoke] = React.useState(false);
    const [showSchedule, setShowSchedule] = React.useState(false);
    const [showUnassign, setShowUnassign] = React.useState(false);
    const [selectedNonLaunched, setSelectedNonLaunched] = React.useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = React.useState(false);

    const { user } = useSelector(state => state.user);

    const allowDeletion = user.companySettings?.development?.allowDeletionOfPastReviews;

    React.useEffect(() => {
        const visible = selected.filter(s =>
            data?.scheduledReviews.some(r => r.id === s && new Date(r.launch) > new Date())
        );

        setSelectedNonLaunched(visible.length > 0);
    }, [selected]);

    const api = {
        poke: confirm => {
            const visible = selected.filter(s => data?.scheduledReviews.some(r => r.id === s));
            if (confirm) {
                apiPatch('review-poke', visible, user.guid, () => {
                    dispatch(setShowSuccess(SystemText('ReviewTracker/Tabs/Reviews/Poke/Success')));
                });
            }

            setShowPoke(false);
        },
        unassign: confirm => {
            if (confirm) {
                const visible = selected.filter(s => data?.scheduledReviews.some(r => r.id === s));
                apiPatch('review-unassign', visible, user.guid, () => {
                    dispatch(setShowSuccess(SystemText('ReviewTracker/Tabs/Reviews/Unassign/Success')));
                    reload();
                });
            }

            setShowUnassign(false);
        },
        delete: confirm => {
            if (confirm) {
                apiDelete(`review-delete/${showDeleteWarning}`, null, user.guid, () => {
                    dispatch(setShowSuccess(SystemText('ReviewTracker/Tabs/Reviews/Delete/Success')));
                    reload();
                });
            }

            setShowDeleteWarning(false);
        },
    };

    const na = SystemText('ReviewTracker/Report/NA');

    const check = i => {
        const temp = [...selected];

        if (temp.some(x => x === i)) {
            setSelected(temp.filter(x => x !== i));
        } else {
            setSelected(temp.concat([i]));
        }
    };

    const sortProps = {
        sortBy,
        descending,
        setSortBy,
        setDescending,
    };

    const handleButtonClick = React.useCallback(
        e => {
            const btn = DomHelpers.getEventButton(e);
            const reviews = data?.scheduledReviews.filter(d => selected.some(s => s === d.id));

            switch (btn.value) {
                case 'unassign':
                    setShowUnassign(true);
                    break;
                case 'poke':
                    setShowPoke(true);
                    break;
                case 'edit':
                    setShowSchedule(reviews);
                    break;
                default:
                    break;
            }
        },
        [selected, data?.scheduledReviews]
    );

    const statusConfig = {
        C: { label: 'ReviewTracker/Tabs/Details/Completed', count: data?.completed },
        M: { label: 'ReviewTracker/Tabs/Details/AwaitingManager', count: data?.awaitingManager },
        U: { label: 'ReviewTracker/Tabs/Details/AwaitingUser', count: data?.awaitingUser },
        O: { label: 'ReviewTracker/Tabs/Details/Outstanding', count: data?.outstanding },
        D: { label: 'ReviewTracker/Tabs/Details/Overdue', count: data?.overdue },
    };

    const config = statusConfig[status] || {
        label: 'ReviewTracker/Tabs/Details/Scheduled',
        count: data?.scheduled,
    };

    const statusLabel = SystemText(config.label);
    const statusCount = config.count;

    const isPrism = user.companySettings?.prism_UI;
    const reviewLink = review => {
        if (isPrism) {
            return `/team-member/${review.profile_Id}?tab=reviews&reviewId=${review.id}`;
        }

        return `/develop/reviews/${review.profile_Id}`;
    };

    return (
        <div key="courses">
            <div>
                <div className="table-btns">
                    <button
                        className="btn btn-sm btn-danger"
                        disabled={!selected?.length}
                        data-testid="button-unassign-reviews"
                        value="unassign"
                        onClick={handleButtonClick}
                    >
                        {SystemText('ReviewTracker/Tabs/Reviews/Buttons/Unassign')}
                    </button>
                    <button
                        className="btn btn-sm btn-info"
                        disabled={!selected?.length}
                        data-testid="button-poke-reviews"
                        value="poke"
                        onClick={handleButtonClick}
                    >
                        {SystemText('ReviewTracker/Tabs/Reviews/Buttons/Poke')}
                    </button>
                    <button
                        className="btn btn-sm btn-info"
                        disabled={!selected?.length}
                        data-testid="button-poke-edit"
                        value="edit"
                        onClick={handleButtonClick}
                    >
                        {SystemText('ReviewTracker/Tabs/Reviews/Buttons/Edit')}
                    </button>
                    {status !== null && (
                        <button
                            className="btn btn-sm btn-info"
                            data-testid="button-clear-status"
                            onClick={() => setStatus(null)}
                        >
                            <i className="fa fa-times mr10" />
                            {statusLabel} ({statusCount})
                        </button>
                    )}
                </div>
            </div>
            {data?.recordcount > 0 ? (
                <div>
                    {loading ? (
                        <Loading />
                    ) : (
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th width="1">
                                        <input
                                            type="checkbox"
                                            checked={selectAll}
                                            data-testid="checkbox-select-all"
                                            onChange={() => setSelectAll(!selectAll)}
                                        />
                                    </th>
                                    <th width="1"></th>
                                    <th>
                                        <TableSorter default column="list_name" {...sortProps}>
                                            {SystemText('ReviewTracker/Tabs/Reviews/Table/Headers/TeamMember')}
                                        </TableSorter>
                                    </th>
                                    <th>
                                        <TableSorter column="created" {...sortProps}>
                                            {SystemText('ReviewTracker/Tabs/Reviews/Table/Headers/Created')}
                                        </TableSorter>
                                    </th>
                                    <th>
                                        <TableSorter column="launch" {...sortProps}>
                                            {SystemText('ReviewTracker/Tabs/Reviews/Table/Headers/Launch')}
                                        </TableSorter>
                                    </th>
                                    <th>
                                        <TableSorter column="scheduled" {...sortProps}>
                                            {SystemText('ReviewTracker/Tabs/Reviews/Table/Headers/Scheduled')}
                                        </TableSorter>
                                    </th>
                                    {user.reviewTypes?.some(x => x === 'U' || x === 'UM') && (
                                        <th>
                                            <TableSorter column="userdone" {...sortProps}>
                                                {SystemText('ReviewTracker/Tabs/Reviews/Table/Headers/UserDone')}
                                            </TableSorter>
                                        </th>
                                    )}
                                    {user.reviewTypes?.some(x => x === 'M' || x === 'UM') && (
                                        <th>
                                            <TableSorter column="managerdone" {...sortProps}>
                                                {SystemText('ReviewTracker/Tabs/Reviews/Table/Headers/ManagerDone')}
                                            </TableSorter>
                                        </th>
                                    )}
                                    <th>
                                        <TableSorter column="submission" {...sortProps}>
                                            {SystemText('ReviewTracker/Tabs/Reviews/Table/Headers/Completed')}
                                        </TableSorter>
                                    </th>
                                    {user.administrator && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {data?.scheduledReviews.map((review, i) => {
                                    review.participants = review.participants || 'UM';

                                    return (
                                        <tr key={`progress-${review.id}-${i}`}>
                                            <td width="1">
                                                <input
                                                    type="checkbox"
                                                    data-testid="checkbox-select-row"
                                                    checked={selected.some(x => x === review.id)}
                                                    onChange={() => check(review.id)}
                                                    disabled={review.submission || (review.coach_Id && !user.administrator)}
                                                />
                                            </td>
                                            <td width="1" className="no-wrap">
                                                <Help
                                                    id={`help-${review.id}`}
                                                    target={
                                                        <i className={`fa fa-${review.complete ? 'star' : 'star-half'}`} />
                                                    }
                                                >
                                                    <div className={review.complete ? '' : 'mb10'}>
                                                        {review.complete
                                                            ? SystemText('ReviewTracker/Tabs/Reviews/Table/FullReview')
                                                            : SystemText('ReviewTracker/Tabs/Reviews/Table/PartReview')}
                                                    </div>
                                                    {review.competencies.split(',').map((c, j) => (
                                                        <div key={`comp-$[i}-${j}]`}>{c}</div>
                                                    ))}
                                                </Help>
                                                {review.coach_Id && (
                                                    <Help
                                                        id={`help-coach-${review.id}`}
                                                        target={<i className="fa fa-whistle ml10" />}
                                                    >
                                                        {SystemText(
                                                            'ReviewTracker/Tabs/Reviews/Table/Headers/Created/Coach'
                                                        )}
                                                    </Help>
                                                )}
                                            </td>
                                            <td>
                                                <Link to={reviewLink(review)}>
                                                    <BlobImage
                                                        height="30"
                                                        className="img-circle mr10"
                                                        folder="avatar"
                                                        initials={review.initials}
                                                        key={review.avatar}
                                                        src={review.avatar}
                                                        alt={review.full_Name}
                                                    />
                                                    {review.full_Name}
                                                </Link>
                                            </td>
                                            <td>
                                                <span className="flex felx-center">
                                                    <span>{formatDate(review.created)}</span>
                                                </span>
                                            </td>
                                            <td>
                                                <span className="flex felx-center">
                                                    <span>{formatDateTime(review.launch)}</span>
                                                </span>
                                            </td>
                                            <td>
                                                <span>
                                                    {formatDate(review.scheduled)}
                                                    {isPast(endOfDay(review.scheduled)) && !review.submission && (
                                                        <Help
                                                            id={`overduehelp-${review.id}`}
                                                            target={
                                                                <i
                                                                    title={SystemText('ReviewTracker/Tabs/Reviews/Overdue')}
                                                                    className="ml10 fa fa-warning text-danger"
                                                                />
                                                            }
                                                        >
                                                            {review.reminders
                                                                ? SystemText(
                                                                      'ReviewTracker/Tabs/Reviews/Table/Overdue/Reminders',
                                                                      review.reminders,
                                                                      e4Format(review.last_Reminder)
                                                                  )
                                                                : SystemText(
                                                                      'ReviewTracker/Tabs/Reviews/Table/Overdue/NoReminders'
                                                                  )}
                                                        </Help>
                                                    )}
                                                </span>
                                            </td>
                                            {user.reviewTypes?.some(x => x === 'U' || x === 'UM') && (
                                                <td>
                                                    <span>
                                                        {['U', 'UM'].some(x => x === review.participants)
                                                            ? formatDate(review.userDone)
                                                            : na}
                                                    </span>
                                                </td>
                                            )}
                                            {user.reviewTypes?.some(x => x === 'M' || x === 'UM') && (
                                                <td>
                                                    <span>
                                                        {['M', 'UM'].some(x => x === review.participants)
                                                            ? formatDate(review.managerDone)
                                                            : na}
                                                    </span>
                                                    {review.wrong_Manager && (
                                                        <Help
                                                            id={`help-manager-${review.id}`}
                                                            target={<i className="fa fa-warning text-warning ml10" />}
                                                        >
                                                            {SystemText(
                                                                'ReviewTracker/Tabs/Reviews/Table/Review/WrongManager'
                                                            )}
                                                        </Help>
                                                    )}
                                                </td>
                                            )}
                                            <td>
                                                <span>
                                                    {formatDate(review.submission)}
                                                    {review.submission && review.submission > review.scheduled && (
                                                        <Help
                                                            id={`latehelp-${review.id}`}
                                                            target={<i className="ml10 fa fa-warning text-danger" />}
                                                        >
                                                            {SystemText('ReviewTracker/Tabs/Reviews/Late')}
                                                        </Help>
                                                    )}
                                                </span>
                                            </td>
                                            {user.administrator && allowDeletion && (
                                                <td>
                                                    {review.submission && (
                                                        <button
                                                            data-testid="button-review-delete"
                                                            className="btn btn-link"
                                                            onClick={() => setShowDeleteWarning(review.id)}
                                                        >
                                                            <i className="fa fa-trash text-danger" />
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    <div className="ml10 mtm5 mb10">
                        <i className={`fa ${selected.length ? 'fa-check text-success' : 'fa-times text-danger'} mr5`} />
                        <span>
                            {SystemText(
                                'ReviewTracker/Tabs/Reviews/Table/Selected',
                                selected.filter(s => data?.scheduledReviews.some(r => r.id === s)).length || '0'
                            )}
                        </span>
                    </div>
                    <div className="text-center mb10 mtm10">
                        <Paging recordCount={data.recordcount} page={page} goToPage={setPage} pageSize={20} />
                    </div>
                </div>
            ) : (
                <NoResults
                    title={SystemText('ReviewTracker/Tabs/Reviews/NoResults/Title')}
                    message={SystemText('ReviewTracker/Tabs/Reviews/NoResults/Message')}
                />
            )}
            <DeleteWarning
                message={SystemText('ReviewTracker/Tabs/Reviews/Delete/Warning')}
                show={showDeleteWarning}
                close={confirm => {
                    api.delete(confirm);
                }}
            />
            <DeleteWarning
                message={SystemText('ReviewTracker/Tabs/Reviews/Unassign/Warning')}
                show={showUnassign}
                close={confirm => {
                    api.unassign(confirm);
                }}
            />
            {selectedNonLaunched ? (
                <>
                    <Modal
                        show={showPoke}
                        title={SystemText('General/Controls/Warning/Title/Warning')}
                        close={() => setShowPoke(false)}
                        footer={null}
                    >
                        <div className="flex flex-center">
                            <i className="fa fa-warning text-danger text-lg mr10" />
                            <span>{SystemText('ReviewTracker/Tabs/Reviews/Poke/Error')}</span>
                        </div>
                    </Modal>
                </>
            ) : (
                <DeleteWarning
                    message={SystemText('ReviewTracker/Tabs/Reviews/Poke/Warning')}
                    show={showPoke}
                    mode="confirm"
                    close={confirm => {
                        api.poke(confirm);
                    }}
                />
            )}
            {showSchedule && (
                <ScheduleEdit
                    key={`schedule-edit-${showSchedule ? showSchedule.join('-') : 'no'}`}
                    show={showSchedule}
                    users={users}
                    close={() => setShowSchedule(false)}
                    onSchedule={reload}
                />
            )}
        </div>
    );
};
export default Reviews;
